import type { Issue, TranscriptionQueueJob } from "./schema";

export function isRecordingPending(job: TranscriptionQueueJob): boolean {
  return !["completed", "canceled", "superseded"].includes(job.status);
}

export function recordingOrder(a: TranscriptionQueueJob, b: TranscriptionQueueJob): number {
  return a.audioVersion - b.audioVersion || a.createdAt.localeCompare(b.createdAt) || a.jobId.localeCompare(b.jobId);
}

/** Compatibility fields are a projection, never the identity of a recording. */
export function projectRecordings(issue: Issue, jobs: TranscriptionQueueJob[], now: string): Issue {
  const current = jobs.filter((job) => job.voiceBatchId === issue.voiceBatchId).sort(recordingOrder);
  const pending = current.filter(isRecordingPending);
  const first = pending[0];
  const next: Issue = {
    ...issue, updatedAt: now, pendingRecordingCount: pending.length,
    pendingAiDescription: undefined, pendingSuggestedDescription: undefined,
    pendingTranscript: undefined, pendingCandidateJobId: undefined,
    activeTranscriptionJobId: first?.jobId, audioBlobKey: first?.audioBlobKey,
    audioMimeType: first?.audioMimeType, audioDurationMs: first?.audioDurationMs,
    audioSizeBytes: first?.audioSizeBytes,
    transcriptionError: pending.find((job) => job.status === "error")?.lastError
  };
  if (pending.length) {
    next.transcriptionStatus = pending.some((job) => job.status === "error") ? "error" :
      pending.some((job) => job.status === "uploading" || job.status === "transcribing") ? "transcribing" : "queued";
    return next;
  }
  const completed = current.filter((job) => job.status === "completed" && !job.reviewedAt);
  if (completed.length) {
    // Keep every result in its recording row. Never truncate or lose a later clip.
    const spoken = completed.map((job) => job.suggestedDescription || job.transcript || "").filter(Boolean).join("\n");
    const combined = [issue.description, spoken].filter(Boolean).join("\n");
    if (combined) {
      next.pendingTranscript = completed.map((job) => job.transcript || "").filter(Boolean).join("\n");
      next.pendingAiDescription = combined;
      next.pendingSuggestedDescription = combined;
      next.pendingCandidateJobId = completed.at(-1)?.jobId;
    }
    next.transcriptionStatus = "needs_review";
  } else if (issue.transcriptionStatus !== "approved") {
    next.transcriptionStatus = issue.description?.trim() ? "needs_review" : "needs_note";
  }
  return next;
}
